import * as Styled from './ViewPhotoModal.styles'

import { Box, Divider, Stack, Typography } from '@mui/material'

import BaseModal from '../BaseModal/BaseModal'
import { Contact, ReactionSelector, ReactionSummary } from '../../components'
import {
  GET_PHOTO_REACTIONS,
  POST_PHOTO_REACTION,
} from '../../services/endpoints'
import { format } from 'date-fns'
import { useTheme, useMediaQuery } from '@mui/material'
import { useEffect, useState } from 'react'
import { PhotoReaction } from '../../types/photoreaction'
import axiosAPI from '../../services/axios'
import { S3Image } from '../../types/s3image'

type ViewPhotoModalProps = {
  open: any
  onClose: any
  selectedImg: S3Image
  caption: string
  currentTime: string
  uploaderName: string
  eventId: string
  userEmail: string
  photoId: string
}

const ViewPhotoModal = ({
  open,
  onClose,
  selectedImg,
  caption,
  currentTime,
  uploaderName,
  eventId,
  userEmail,
  photoId,
}: ViewPhotoModalProps) => {
  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'))

  const [photoReactions, setPhotoReactions] = useState<{
    [key: string]: { [reaction: string]: number }
  }>({})
  const [isUpdatingReaction, setIsUpdatingReaction] = useState(false)

  const postPhotoReaction = async (data: PhotoReaction) => {
    const url = POST_PHOTO_REACTION(data.eventId)
    return axiosAPI.post(url, data)
  }

  const fetchPhotoReactions = async (
    eventId: string,
    imageKey: string,
  ): Promise<{ [reaction: string]: number }> => {
    const url = GET_PHOTO_REACTIONS(eventId, imageKey)

    const response = await axiosAPI.get(url)
    const rawReactions = response.data.result || {}

    // Convert backend response into a flat reaction count map

    const reactionCounts: { [reaction: string]: number } = {}

    for (const [reactionName, reactionData] of Object.entries(rawReactions)) {
      const users = (reactionData as any).users || []
      // console.log(`Reaction: ${reactionName}, Count: ${users.length}`)
      reactionCounts[reactionName] = users.length
    }

    return reactionCounts
  }

  // Load reactions when the modal opens or when the selected image changes

  useEffect(() => {
    const loadReactions = async () => {
      try {
        const backendReactions = await fetchPhotoReactions(
          eventId,
          selectedImg.key,
        )

        setPhotoReactions((prev) => ({
          ...prev,
          [selectedImg.key]: backendReactions,
        }))
      } catch (error) {
        console.error('Failed to load photo reactions:', error)
      }
    }
    if (open && selectedImg?.key) {
      loadReactions()
    }
  }, [open, selectedImg?.key, eventId])

  useEffect(() => {
    if (!open || !selectedImg?.key) return

    const interval = setInterval(async () => {
      try {
        const backendReactions = await fetchPhotoReactions(
          eventId,
          selectedImg.key,
        )
        setPhotoReactions((prev) => ({
          ...prev,
          [selectedImg.key]: backendReactions,
        }))
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [open, selectedImg?.key, eventId])

  const handleReactionUpdate = async (newReaction: string) => {
    setIsUpdatingReaction(true)

    try {
      const payload: PhotoReaction = {
        eventId,
        photoId,
        reaction: newReaction,
        name: userEmail,
      }

      // Send reaction to backend
      await postPhotoReaction(payload)

      //  Immediately fetch the updated reactions from backend
      const updatedReactions = await fetchPhotoReactions(
        eventId,
        selectedImg.key,
      )

      //  Replace local state with the latest from backend
      setPhotoReactions((prevReactions) => ({
        ...prevReactions,
        [photoId]: updatedReactions,
      }))
    } catch (error) {
      console.error('Failed to post or fetch updated photo reactions:', error)
    } finally {
      setIsUpdatingReaction(false)
    }
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      tabletWidth="80%"
      desktopWidth="940px"
      fullScreenMobile
      overflow="auto"
    >
      <Stack
        direction="column"
        width="100%"
        style={{ marginTop: theme.spacing(3) }}
      >
        {selectedImg && <Styled.SelectedImage src={selectedImg.s3Url} alt="" />}
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <ReactionSummary
            reactions={photoReactions[selectedImg.key] || {}}
          ></ReactionSummary>
          {!isMobile && (
            <ReactionSelector
              onReactionSelect={handleReactionUpdate}
              isDisabled={isUpdatingReaction}
            ></ReactionSelector>
          )}
        </Box>

        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Contact
            variant="modalBody"
            size={24}
            spacing={theme.spacing(1)}
            name={uploaderName}
          />

          <Styled.CurrentTimeText>
            {currentTime !== '' &&
              format(new Date(currentTime), 'hh:mma - MM/dd/yy')}
          </Styled.CurrentTimeText>
        </Box>
        <Typography
          variant="body2"
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            boxSizing: 'border-box',
          }}
        >
          {caption}
        </Typography>

        {isMobile && (
          <>
            <Divider sx={{ marginBottom: 3, marginTop: 3 }}></Divider>
            <ReactionSelector
              onReactionSelect={handleReactionUpdate}
            ></ReactionSelector>
          </>
        )}
      </Stack>
    </BaseModal>
  )
}

export default ViewPhotoModal
